"use client";

import { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";

import "swiper/css";
import "swiper/css/pagination";
import CurvySliderSlide from "./CurvySliderSlide";
import { CurvySliderItem, CurvySliderSection } from "@/lib/navigation";

const DEFAULT_SUB_TITLE = "Wonderful Place For You";
const DEFAULT_TITLE = "Why Central Holidays";

interface CurvySliderProps {
  categories: CurvySliderItem[];
  /** CMS header copy; each field falls back to the static copy when empty. */
  section?: CurvySliderSection;
}

export default function CurvySlider({ categories, section }: CurvySliderProps) {
  const [ready, setReady] = useState(false);
  const subTitle = section?.subTitle || DEFAULT_SUB_TITLE;
  const title = section?.title || DEFAULT_TITLE;

  useEffect(() => {
    let animFrameId: number;

    function calculateWheel() {
      const slides = document.querySelectorAll<HTMLElement>(
        ".categorySlider .single",
      );

      slides.forEach((slide) => {
        const rect = slide.getBoundingClientRect();
        const r = window.innerWidth * 0.5 - (rect.x + rect.width * 0.5);
        let ty = Math.abs(r) * 0.1 - rect.width * 0.1;

        if (ty < 0) ty = 0;

        slide.style.transform = `translate(0, ${ty}px) rotate(${-r * 0.01}deg)`;
        slide.style.transformOrigin = r < 0 ? "left top" : "right top";
      });
    }

    function raf() {
      animFrameId = requestAnimationFrame(raf);
      calculateWheel();
    }

    raf();

    return () => cancelAnimationFrame(animFrameId);
  }, []);

  if (!categories?.length) return null;

  return (
    <section className="category-area bg-top-center">
      <div className="container th-container">
        <div className="title-area text-center">
          <span className="sub-title">{subTitle}</span>
          <h2 className="sec-title">{title}</h2>
        </div>
        <Swiper
          className="th-slider has-shadow categorySlider"
          style={{ opacity: ready ? 1 : 0, transition: "opacity 0.3s ease" }}
          modules={[Pagination, Autoplay]}
          onInit={() => setReady(true)}
          spaceBetween={50}
          loop={categories.length > 5}
          autoplay={{
            delay: 1200,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          centerInsufficientSlides
          pagination={
            categories.length > 5
              ? { el: ".category-slider-pagination", clickable: true }
              : false
          }
          breakpoints={{
            0: { slidesPerView: 1 },
            576: { slidesPerView: 1 },
            768: { slidesPerView: 2 },
            992: { slidesPerView: 3 },
            1200: { slidesPerView: 3 },
            1400: { slidesPerView: 5 },
          }}
        >
          {categories.map((cat, idx) => (
            <SwiperSlide key={cat.slideId || `${cat.url}-${cat.order ?? idx}`}>
              <CurvySliderSlide item={cat} />
            </SwiperSlide>
          ))}

          <div className="slider-pagination category-slider-pagination"></div>
        </Swiper>
      </div>
    </section>
  );
}
